import IndicatorCategoryTag from "components/financials/IndicatorCategoryTag";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import { INDICATOR_SEARCH_QUERY_ATOM } from "components/financials/modern/indicators/atoms";
import getIndicatorDocumentation from "components/financials/modern/indicators/getIndicatorDocumentation";
import classNames from "components/ui/classNames";
import { useAtomValue } from "jotai";
import type { WithoutTypename } from "lib/types/graphql";
import Highlighter from "react-highlight-words";
import { FormattedMessage, useIntl } from "react-intl";

interface IndicatorDetailsProps {
  isSelected?: boolean;
  viewerCanSelect?: boolean;
  shouldHighlightBox?: boolean;
  indicator: WithoutTypename<IIndicatorFieldsFragment>;
  onClick?: () => void;
}
export default function IndicatorDetails({
  isSelected,
  viewerCanSelect,
  shouldHighlightBox = true,
  indicator,
  onClick,
}: IndicatorDetailsProps) {
  const intl = useIntl();
  const documentation = getIndicatorDocumentation(intl, indicator);
  const searchQuery = useAtomValue(INDICATOR_SEARCH_QUERY_ATOM);
  const normalizedWords = searchQuery
    .split(" ")
    .map((word) => word.trim())

    .filter(Boolean);
  const searchWords = [searchQuery, ...normalizedWords].filter((token) => token.length >= 3);
  const hasSearchQuery = searchWords.length > 0;

  return (
    <div
      data-selected={isSelected || undefined}
      className={classNames("group w-full space-y-2 rounded-xl px-3 py-3", {
        "data-[selected]:bg-neutral-50 dark:data-[selected]:bg-emerald-900/30": shouldHighlightBox,
      })}
    >
      <div className="flex items-center space-x-2 text-ellipsis">
        <h1>
          <Highlighter
            className="text-sm font-bold text-gray-700 dark:text-slate-400 dark:group-data-[selected]:text-white"
            highlightClassName="bg-teal-800/5 text-teal-600 font-bold px-1.5 py-0.5 rounded-md dark:bg-teal-800/30 dark:text-teal-300"
            searchWords={searchWords}
            autoEscape={false}
            textToHighlight={documentation.name}
          />
        </h1>
        <IndicatorCategoryTag variant="outline" indicator={indicator} />
        <button
          type="button"
          disabled={!isSelected && !viewerCanSelect}
          onClick={onClick}
          className={classNames(
            "rounded-full bg-indigo-600/5 px-2 py-0.5 text-xs font-bold text-indigo-600 dark:bg-indigo-600/10 dark:text-indigo-300",
            "hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-800 dark:hover:text-white",
            "border border-transparent",
            "group-data-[selected]:border-gray-800/20 group-data-[selected]:bg-gray-400/10 group-data-[selected]:text-gray-600",
            "dark:group-data-[selected]:border-transparent dark:group-data-[selected]:bg-red-600/20 dark:group-data-[selected]:text-white",
            "group-data-[selected]:hover:bg-gray-600 group-data-[selected]:hover:text-white",
            "dark:group-data-[selected]:hover:bg-red-600 dark:group-data-[selected]:hover:text-white",
          )}
        >
          {isSelected ? (
            <FormattedMessage defaultMessage="Remover" id="TolqfU" />
          ) : (
            <FormattedMessage defaultMessage="Selecionar" id="LJC6Pz" />
          )}
        </button>
      </div>

      <p>
        <Highlighter
          className={classNames(
            "line-clamp-2 text-sm font-normal tracking-wide text-gray-500",
            "group-data-[selected]:text-gray-300",
            "dark:text-slate-500",
          )}
          highlightClassName="bg-teal-800/5 text-teal-600 font-bold px-1.5 py-0.5 rounded-md"
          unhighlightClassName={hasSearchQuery ? "text-gray-400 italic" : undefined}
          searchWords={searchWords}
          autoEscape={false}
          textToHighlight={documentation.description}
        />
      </p>
    </div>
  );
}
